import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { CheckCircle2, XCircle, Loader2, Calendar, Clock, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

interface Booking {
  id?: number;
  start_time: string;
  end_time: string;
  status: string;
  teacher_first_name: string;
  teacher_last_name: string;
  student_name?: string;
  cancellation_token?: string;
}

export default function AppointmentConfirm() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [autoConfirmTriggered, setAutoConfirmTriggered] = useState(false);

  // Fetch booking data (can be single booking or array for batch confirmation)
  const { data: bookingData, isLoading, error } = useQuery(
    ['appointments', 'confirm', token],
    async () => {
      const response = await api.get(`/appointments/public/confirm/${token}`);
      return response.data;
    },
    { enabled: !!token, retry: 1 }
  );

  // Handle both single booking and batch bookings
  const bookings = bookingData?.is_batch 
    ? (Array.isArray(bookingData.data) ? bookingData.data : [bookingData.data])
    : (bookingData?.data ? [bookingData.data] : []);
  const booking = bookings.length > 0 ? bookings[0] : null;
  const isBatch = bookingData?.is_batch || bookings.length > 1;

  // Confirm mutation
  const confirmMutation = useMutation(
    async () => {
      const response = await api.post(`/appointments/public/confirm/${token}`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setConfirmed(true);
        const count = data?.confirmed_count || bookings.length || 1;
        toast.success(`${count} Termin${count > 1 ? 'e' : ''} erfolgreich bestätigt!`);
        setTimeout(() => {
          navigate('/appointments/all');
        }, 3000);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Bestätigung');
      }
    }
  );

  useEffect(() => {
    // Check if all bookings are confirmed
    if (bookings.length > 0 && bookings.every((b: Booking) => b.status === 'confirmed')) {
      setConfirmed(true);
    } else if (booking && booking.status === 'confirmed') {
      setConfirmed(true);
    }
  }, [bookings, booking]);

  // Check if bookings can still be confirmed automatically
  const allConfirmed = bookings.length > 0 && bookings.every((b: Booking) => b.status === 'confirmed');
  const hasPendingBookings = bookings.some((b: Booking) => b.status === 'pending_confirmation');

  useEffect(() => {
    if (!token) return;
    if (confirmed || allConfirmed) return;
    if (!hasPendingBookings) return;
    if (confirmMutation.isLoading || autoConfirmTriggered) return;

    setAutoConfirmTriggered(true);
    confirmMutation.mutate();
  }, [
    token,
    confirmed,
    allConfirmed,
    hasPendingBookings,
    confirmMutation.isLoading,
    confirmMutation.mutate,
    autoConfirmTriggered
  ]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || bookings.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Buchung nicht gefunden</h1>
          <p className="text-gray-600 mb-4">
            Die angeforderte Buchung existiert nicht oder ist bereits abgelaufen.
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  if (confirmed || allConfirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white rounded-lg shadow-lg p-8">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">
            {isBatch ? `${bookings.length} Termine bestätigt!` : 'Termin bestätigt!'}
          </h1>
          <p className="text-gray-600 mb-4">
            {isBatch 
              ? `Ihre ${bookings.length} Termine wurden erfolgreich bestätigt. Sie erhalten eine E-Mail mit allen Details und Stornierungs-Links.`
              : 'Ihr Termin wurde erfolgreich bestätigt. Sie erhalten eine E-Mail mit allen Details und einem Stornierungs-Link.'}
          </p>
          <div className="bg-gray-50 rounded p-4 mb-4 text-left max-h-96 overflow-y-auto">
            {bookings.map((b: Booking, idx: number) => (
              <div key={b.id || idx} className={idx > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold">
                    {format(parseISO(b.start_time), 'dd.MM.yyyy', { locale: de })}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span>
                    {format(parseISO(b.start_time), 'HH:mm', { locale: de })} -{' '}
                    {format(parseISO(b.end_time), 'HH:mm', { locale: de })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  <span>
                    {b.teacher_first_name} {b.teacher_last_name}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {bookings.length > 0 && bookings[0].cancellation_token && (
            <div className="mb-4">
              {isBatch ? (
                <div className="text-sm">
                  <p className="mb-2">Termine stornieren:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {bookings.map((b: Booking, idx: number) => (
                      <li key={b.id || idx}>
                        <a
                          href={`/appointments/cancel/${b.cancellation_token}`}
                          className="text-red-600 hover:text-red-800 underline"
                        >
                          Termin {idx + 1} stornieren ({b.teacher_first_name} {b.teacher_last_name})
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <a
                  href={`/appointments/cancel/${bookings[0].cancellation_token}`}
                  className="text-red-600 hover:text-red-800 text-sm underline"
                >
                  Termin stornieren
                </a>
              )}
            </div>
          )}
          <p className="text-sm text-gray-500">
            Sie werden zur Übersicht weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  // Only show error if no bookings are pending and not all are confirmed
  if (!hasPendingBookings && !allConfirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Buchung nicht bestätigbar</h1>
          <p className="text-gray-600 mb-4">
            Diese Buchung kann nicht mehr bestätigt werden.
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold mb-4">
          {isBatch ? `${bookings.length} Termine bestätigen` : 'Termin bestätigen'}
        </h1>
        
        <div className="bg-gray-50 rounded p-4 mb-6 max-h-96 overflow-y-auto">
          {bookings.map((b: Booking, idx: number) => (
            <div key={b.id || idx} className={idx > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
              <div className="mb-3">
                <div className="text-sm text-gray-600 mb-1">
                  {isBatch ? `Termin ${idx + 1}` : 'Termin'}
                </div>
                <div className="font-semibold">
                  {format(parseISO(b.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} -{' '}
                  {format(parseISO(b.end_time), 'HH:mm', { locale: de })}
                </div>
              </div>
              <div className="mb-3">
                <div className="text-sm text-gray-600 mb-1">Lehrer</div>
                <div className="font-semibold">
                  {b.teacher_first_name} {b.teacher_last_name}
                </div>
              </div>
              {b.student_name && (
                <div>
                  <div className="text-sm text-gray-600 mb-1">Schüler</div>
                  <div className="font-semibold">{b.student_name}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-6 text-sm text-yellow-800">
          {confirmMutation.isLoading
            ? `Bestätigung läuft automatisch. Bitte warten...`
            : `Die Bestätigung wird automatisch ausgelöst. Wenn etwas schiefgeht, können Sie unten erneut bestätigen.`}
        </div>

        {confirmMutation.isLoading && (
          <div className="w-full bg-green-600 text-white px-6 py-3 rounded flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Wird bestätigt...
          </div>
        )}

        {confirmMutation.isError && (
          <button
            onClick={() => confirmMutation.mutate()}
            disabled={confirmMutation.isLoading}
            className="w-full bg-green-600 text-white px-6 py-3 rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-5 h-5" />
            {isBatch ? `Erneut: alle ${bookings.length} Termine bestätigen` : 'Erneut bestätigen'}
          </button>
        )}
      </div>
    </div>
  );
}

